import React, { useMemo, useState } from 'react';
import {
  X, Trash2, Users2, WifiOff, Loader2, List, ChevronDown, ChevronRight,
  Folder as FolderIcon, FolderPlus, FolderInput, Check,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { toast } from '@shared/components/ui/sonner';
import { cn } from '@shared/lib/utils';
import { Note, CollaboratorPermission, Folder } from '@shared/types';
import { useSharedNotesQuery } from '@shared/hooks/useSharedNotesQuery';
import { useFoldersQuery, useCreateFolderMutation } from '@shared/hooks/useFoldersQuery';
import ConfirmDeleteDialog from '@shared/components/common/ConfirmDeleteDialog';

interface RecentNotesPanelProps {
  notes: Note[];
  onSelectNote: (note: Note) => void;
  /** Open a note that was shared with the user (permission drives read-only vs edit). */
  onSelectSharedNote?: (note: Note, permission: CollaboratorPermission) => void;
  /** Move a note into a folder. */
  onMoveNote?: (note: Note, folderId: string) => void;
  onClose?: () => void;
  onDeleteNote: (note_id: string) => void;
  selectedNoteId?: string;
}

// Utility to extract a preview string from Tiptap JSON content
function getNotePreview(content: any): string {
  let json;
  if (!content) return '';
  if (typeof content === 'string') {
    try {
      json = JSON.parse(content);
    } catch {
      return content.slice(0, 100); // fallback: show first 100 chars
    }
  } else {
    json = content;
  }
  if (!json.content) return '';
  const node = json.content.find(
    (n: any) =>
      (n.type === 'heading' || n.type === 'paragraph') &&
      n.content &&
      n.content.length > 0
  );
  if (node) {
    return node.content.map((c: any) => c.text).join(' ').slice(0, 100);
  }
  return '';
}

type Tab = 'mine' | 'shared';
type ViewMode = 'list' | 'folders';

const VIEW_MODE_KEY = 'hintword_notes_view_mode';

const RecentNotesPanel = ({
  notes, onSelectNote, onSelectSharedNote, onMoveNote, onClose, onDeleteNote, selectedNoteId,
}: RecentNotesPanelProps) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('mine');
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) || 'list'
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const noteToDelete = notes.find(n => n.note_id === deleteId);

  const { data: folders = [] } = useFoldersQuery();
  const createFolder = useCreateFolderMutation();

  // Shared notes are online-only; fetch lazily when the tab is opened.
  const { data: shared, isLoading: sharedLoading, isError: sharedError } =
    useSharedNotesQuery(tab === 'shared');
  const sharedNotes = shared?.notes ?? [];
  const sharedPermissions = shared?.permissions ?? {};

  const switchView = (mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch {}
  };

  const toggleFolder = (id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await createFolder.mutateAsync({ name });
      setNewFolderName('');
      setShowNewFolder(false);
      toast.success(`Folder "${name}" created`);
    } catch {
      toast.error(navigator.onLine
        ? 'Failed to create folder'
        : 'Creating folders needs a connection');
    }
  };

  // Sort folders: Default first, then by sequence/name.
  const sortedFolders = useMemo(() =>
    [...folders].sort((a, b) =>
      Number(b.is_default) - Number(a.is_default)
      || (a.sequence ?? 0) - (b.sequence ?? 0)
      || a.name.localeCompare(b.name)
    ), [folders]);

  // Group notes by folder_id; anything unmatched lands in "Unfiled".
  const grouped = useMemo(() => {
    const byFolder = new Map<string, Note[]>();
    const unfiled: Note[] = [];
    const known = new Set(folders.map(f => f.folder_id));
    for (const n of notes) {
      if (n.folder_id && known.has(n.folder_id)) {
        const list = byFolder.get(n.folder_id) ?? [];
        list.push(n);
        byFolder.set(n.folder_id, list);
      } else {
        unfiled.push(n);
      }
    }
    return { byFolder, unfiled };
  }, [notes, folders]);

  const moveMenu = (note: Note) => {
    if (!onMoveNote || folders.length === 0) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-10 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Move to folder"
            onClick={e => e.stopPropagation()}
          >
            <FolderInput className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {sortedFolders.map(f => (
            <DropdownMenuItem
              key={f.folder_id}
              onClick={(e) => { e.stopPropagation(); if (note.folder_id !== f.folder_id) onMoveNote(note, f.folder_id); }}
            >
              <FolderIcon className="mr-2 h-3.5 w-3.5" />
              <span className="truncate">{f.name}</span>
              {note.folder_id === f.folder_id && <Check className="ml-auto h-3.5 w-3.5" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const noteCard = (note: Note, opts: { shared?: boolean }) => (
    <div key={note.note_id || note.title || Math.random()} className="relative group">
      <button
        onClick={() =>
          opts.shared
            ? onSelectSharedNote?.(note, sharedPermissions[note.note_id] ?? 'VIEW')
            : onSelectNote(note)
        }
        className={cn(
          'group relative select-none flex flex-col gap-3 px-4 py-2 rounded-lg bg-muted/50 hover:bg-accent/40 border border-border/10 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/5 active:translate-y-0 active:shadow-md h-full cursor-pointer w-full text-left mb-2',
          note.note_id === selectedNoteId && 'border-2 border-hintword-orange/50 bg-hintword-orange/10'
        )}
      >
        <h3 className="font-medium truncate">{note.title || 'Untitled'}</h3>
        <div className="text-sm text-muted-foreground recent-note-content line-clamp-2">
          {getNotePreview(note.content)}
        </div>
        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
          {note.updated_at && !isNaN(Date.parse(note.updated_at))
            ? new Date(note.updated_at).toLocaleDateString()
            : 'Draft'}
          {opts.shared && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent px-1.5 py-0.5 text-[10px]">
              <Users2 className="h-2.5 w-2.5" />
              {(sharedPermissions[note.note_id] ?? 'VIEW').toLowerCase()}
            </span>
          )}
        </p>
      </button>
      {!opts.shared && moveMenu(note)}
      {!opts.shared && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => { e.stopPropagation(); setDeleteId(note.note_id); }}
          title="Delete note"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      )}
    </div>
  );

  const folderSection = (id: string, name: string, list: Note[], isDefault = false) => {
    const isCollapsed = collapsed.has(id);
    return (
      <div key={id} className="mb-1">
        <button
          onClick={() => toggleFolder(id)}
          className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-semibold hover:bg-accent/40"
        >
          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          <FolderIcon className={cn('h-3.5 w-3.5', isDefault ? 'text-hintword-orange' : 'text-muted-foreground')} />
          <span className="truncate">{name}</span>
          <span className="ml-auto text-xs font-normal text-muted-foreground">{list.length}</span>
        </button>
        {!isCollapsed && (
          <div className="mt-1 pl-2">
            {list.length === 0
              ? <p className="px-2 pb-2 text-xs text-muted-foreground">No notes</p>
              : list.map(n => noteCard(n, {}))}
          </div>
        )}
      </div>
    );
  };

  const folderView = (
    <>
      {sortedFolders.map(f =>
        folderSection(f.folder_id, f.name, grouped.byFolder.get(f.folder_id) ?? [], f.is_default)
      )}
      {grouped.unfiled.length > 0 && folderSection('__unfiled', 'Unfiled', grouped.unfiled)}
      {folders.length === 0 && (
        <p className="px-2 py-4 text-center text-xs text-muted-foreground">
          No folders yet — sync once to load them, or create one below.
        </p>
      )}
      {showNewFolder ? (
        <div className="mt-2 flex items-center gap-2 px-1">
          <Input
            autoFocus
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            placeholder="Folder name…"
            className="h-8 text-sm"
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreateFolder();
              if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); }
            }}
          />
          <Button size="sm" className="h-8" onClick={handleCreateFolder} disabled={!newFolderName.trim() || createFolder.isPending}>
            {createFolder.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
          </Button>
        </div>
      ) : (
        <button
          onClick={() => setShowNewFolder(true)}
          className="mt-1 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent/40 hover:text-foreground"
        >
          <FolderPlus className="h-3.5 w-3.5" /> New folder
        </button>
      )}
    </>
  );

  const tabs = (
    <div className="flex items-center gap-2">
      <div className="flex flex-1 gap-1 rounded-lg bg-muted/60 p-1">
        {([['mine', 'My notes'], ['shared', 'Shared with me']] as [Tab, string][]).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
              tab === id ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {/* View toggle: flat list ⇄ folder groups (my notes only) */}
      {tab === 'mine' && (
        <div className="flex gap-0.5 rounded-lg bg-muted/60 p-1">
          <button
            onClick={() => switchView('list')}
            title="Note view"
            className={cn('rounded-md p-1.5', viewMode === 'list' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => switchView('folders')}
            title="Folder view"
            className={cn('rounded-md p-1.5', viewMode === 'folders' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >
            <FolderIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );

  const listBody = tab === 'mine' ? (
    viewMode === 'folders' ? folderView : <>{notes.map(n => noteCard(n, {}))}</>
  ) : sharedLoading ? (
    <div className="flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading shared notes…
    </div>
  ) : sharedError || !navigator.onLine ? (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground">
      <WifiOff className="h-5 w-5" />
      Shared notes need a connection.
    </div>
  ) : sharedNotes.length === 0 ? (
    <div className="px-4 py-10 text-center text-sm text-muted-foreground">
      Nothing has been shared with you yet.
    </div>
  ) : (
    <>{sharedNotes.map(n => noteCard(n, { shared: true }))}</>
  );

  const header = (title: string) => (
    <div className="flex items-center justify-between pb-3 border-b border-border">
      <h2 className="font-semibold text-lg">{title}</h2>
      {onClose && (
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  const deleteDialog = (
    <ConfirmDeleteDialog
      open={!!deleteId}
      title="Delete Note?"
      description={`Are you sure you want to delete "${noteToDelete?.title || 'this note'}"? This action cannot be undone.`}
      onCancel={() => setDeleteId(null)}
      onConfirm={() => { if (deleteId) { onDeleteNote(deleteId); setDeleteId(null); } }}
      confirmLabel="Delete"
      cancelLabel="Cancel"
    />
  );

  return (
    <>
      {/* Mobile Bottom Sheet */}
      <div className="block md:hidden">
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} />
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-background rounded-t-2xl shadow-lg h-[60vh] transition-transform duration-300 ease-in-out p-4">
          <div className="w-full flex flex-col items-center cursor-grab active:cursor-grabbing pt-2">
            <div className="w-12 h-1 bg-muted rounded-full mb-4" />
          </div>
          {header('Notes')}
          <div className="pt-2">{tabs}</div>
          <div className="overflow-y-auto h-[calc(60vh-140px)] pt-2">{listBody}</div>
          {deleteDialog}
        </div>
      </div>

      {/* Desktop Side Panel */}
      <div className="hidden md:block">
        <div className="fixed top-0 right-0 w-full md:w-[400px] h-screen bg-background border-l border-border shadow-lg z-50 p-4">
          {header('Notes')}
          <div className="pt-3">{tabs}</div>
          <div className="overflow-y-auto h-[calc(100vh-110px)] pt-2">{listBody}</div>
          {deleteDialog}
        </div>
      </div>
    </>
  );
};

export default RecentNotesPanel;
